import { get } from 'svelte/store';
import { allAssetFolders, allAssets } from '$lib/services/assets';
import { getMediaLibraryOptions } from '$lib/services/assets/media-library';
import { allCloudStorageServices } from '$lib/services/integrations/media-libraries';
import { prefs } from '$lib/services/user/prefs';

/**
 * @import { Asset, AssetFolderInfo, ExternalAsset } from '$lib/types/private';
 */

/**
 * Whether the folder lists files on a cloud storage service.
 * @param {AssetFolderInfo | undefined} folder Folder.
 * @returns {boolean} Result.
 */
export const isExternalFolder = (folder) => !!folder?.service;

/**
 * Whether the asset is stored on a cloud storage service.
 * @param {Asset | undefined} asset Asset.
 * @returns {boolean} Result.
 */
export const isExternalAsset = (asset) => !!asset?.folder?.service;

/**
 * Get the service, credentials and settings for an external folder.
 * @param {AssetFolderInfo} folder Folder.
 * @returns {{ service: import('$lib/types/private').MediaLibraryService | undefined,
 * apiKey: string | undefined, settings: Record<string, any> }} Context.
 */
const getContext = ({ service: serviceId = '' }) => ({
  service: allCloudStorageServices[serviceId],
  apiKey: get(prefs)?.apiKeys?.[serviceId],
  settings:
    getMediaLibraryOptions({ libraryName: /** @type {any} */ (serviceId) })?.settings ?? {},
});

/**
 * Whether the external folder can be used, i.e. The user has entered the credentials.
 * @param {AssetFolderInfo | undefined} folder Folder.
 * @returns {boolean} Result.
 */
export const hasExternalCredentials = (folder) => !!folder && !!getContext(folder).apiKey;

/**
 * Convert a service result to an `Asset` so the asset list can show it.
 * @param {AssetFolderInfo} folder Folder.
 * @param {ExternalAsset} item Service result. `id` is the object key.
 * @returns {Asset} Asset.
 */
const toAsset = (folder, { id, fileName, downloadURL, kind, lastModified, size }) =>
  /** @type {Asset} */ ({
    name: fileName.split('/').pop() ?? fileName,
    path: `${folder.internalPath}/${id}`,
    url: downloadURL,
    // The public URL works as a blob URL for previews, downloads and thumbnails
    blobURL: downloadURL,
    sha: `${id}@${lastModified ? new Date(lastModified).getTime() : 0}`,
    size,
    kind,
    folder,
  });

/**
 * Get the object key of an external asset.
 * @param {Asset} asset Asset.
 * @returns {string} Key.
 */
const getKey = ({ path, folder }) => path.slice(`${folder.internalPath}/`.length);

/**
 * Load the files of every external folder into `allAssets`. Folders without credentials are
 * skipped. Safe to call again: previously loaded external assets are replaced.
 */
export const loadExternalAssets = async () => {
  const folders = get(allAssetFolders).filter(isExternalFolder);

  const loaded = await Promise.all(
    folders.map(async (folder) => {
      const { service, apiKey, settings } = getContext(folder);

      if (!service?.search || !apiKey) {
        return [];
      }

      try {
        const items = await service.search('', { apiKey, settings });

        return items.map((item) => toAsset(folder, item));
      } catch (/** @type {any} */ ex) {
        // eslint-disable-next-line no-console
        console.error(ex);

        return [];
      }
    }),
  );

  allAssets.update((assets) => [...assets.filter((a) => !isExternalAsset(a)), ...loaded.flat()]);
};

/**
 * Upload files to the service behind an external folder and add them to `allAssets`.
 * @param {AssetFolderInfo} folder Folder.
 * @param {File[]} files Files.
 * @returns {Promise<Asset[]>} New assets.
 */
export const uploadExternalAssets = async (folder, files) => {
  const { service, apiKey, settings } = getContext(folder);

  if (!service?.upload || !apiKey) {
    throw new Error('The storage service is not configured');
  }

  const items = await service.upload(files, { apiKey, settings });
  const newAssets = items.map((item) => toAsset(folder, item));

  allAssets.update((assets) => [
    ...assets.filter((a) => !newAssets.some((na) => na.path === a.path)),
    ...newAssets,
  ]);

  return newAssets;
};

/**
 * Delete external assets from their service.
 * @param {Asset[]} assets Assets, all stored on services.
 */
export const deleteExternalAssets = async (assets) => {
  /** @type {Map<AssetFolderInfo, Asset[]>} */
  const byFolder = new Map();

  assets.forEach((asset) => {
    byFolder.set(asset.folder, [...(byFolder.get(asset.folder) ?? []), asset]);
  });

  await Promise.all(
    [...byFolder].map(async ([folder, folderAssets]) => {
      const { service, apiKey, settings } = getContext(folder);

      if (!service?.remove || !apiKey) {
        throw new Error('The storage service is not configured');
      }

      await service.remove(folderAssets.map(getKey), { apiKey, settings });
    }),
  );
};
